import type { IntlShape } from "@formatjs/intl";
import { Divider, Empty, Flex, Skeleton } from "antd";
import { useEffect, useRef, useState } from "react";
import type { EmojiEnum } from "../../app/generated-axios-client/myModel/emoji-enum";
import type { CommentNewFiles, UploadedCommentAttachment } from "./CommentComposer";
import { CommentComposer } from "./CommentComposer";
import type { CommentAuthor, CommentEntry, TaskSummary } from "./CommentItem";
import { CommentItem } from "./CommentItem";
import type { CommentReaction } from "./CommentReactions";
import styles from "./CommentsTab.module.css";
import type { PreviewableFile } from "./FilePreviewModal";

export type {
  CommentAuthor,
  CommentEntry,
  CommentNewFiles,
  TaskSummary,
  UploadedCommentAttachment,
};

export type CommentAttachmentTarget = {
  ticketId: string;
  ticketCommentId: string;
  ticketItemId?: string;
};

export type ReactionMutationResult =
  | { succeeded: true; reactionId?: string }
  | { succeeded: false; status?: number };

type CommentsTabProps = {
  ticketId: string;
  currentUserId: number | undefined;
  taskId?: string;
  taskMap?: Record<string, TaskSummary>;
  onList: (ticketId: string, taskId?: string) => Promise<CommentEntry[]>;
  onCreate: (
    ticketId: string,
    content: string,
    userId: number,
    replyTo?: number,
    ticketItem?: string,
    newFiles?: CommentNewFiles
  ) => Promise<void>;
  onUpdate: (id: string, content: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onUploadAttachment: (file: File) => Promise<UploadedCommentAttachment>;
  onAttachFileToComment: (file: File, target: CommentAttachmentTarget) => Promise<void>;
  onAddReaction: (
    commentId: string,
    emoji: EmojiEnum,
    userId: number
  ) => Promise<ReactionMutationResult>;
  onRemoveReaction: (reactionId: string) => Promise<ReactionMutationResult>;
  getDownloadUrl: (file: PreviewableFile) => string;
  intl: IntlShape;
  stickyComposer?: boolean;
  messengerLayout?: boolean;
  highlightedCommentId?: string;
};

const buildPendingReactionKey = (commentId: string, emoji: EmojiEnum) =>
  `${commentId}:${emoji}`;

export const CommentsTab = ({
  ticketId,
  currentUserId,
  taskId,
  taskMap,
  onList,
  onCreate,
  onUpdate,
  onDelete,
  onUploadAttachment,
  onAttachFileToComment,
  onAddReaction,
  onRemoveReaction,
  getDownloadUrl,
  intl,
  stickyComposer = false,
  messengerLayout = false,
  highlightedCommentId,
}: CommentsTabProps) => {
  const composerRef = useRef<HTMLDivElement>(null);
  const highlightedCommentRef = useRef<HTMLDivElement>(null);
  const [comments, setComments] = useState<CommentEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{
    id: number;
    name: string;
    ticketItem?: string | null;
  } | null>(null);
  // The ref blocks a second click before re-render; the state disables the chip visually
  const pendingReactionKeysRef = useRef(new Set<string>());
  const [pendingReactionKeys, setPendingReactionKeys] = useState<string[]>([]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    onList(ticketId, taskId)
      .then((result) => {
        setComments(result);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [ticketId, taskId, onList]);

  useEffect(() => {
    if (loading || !highlightedCommentId) return;
    const frame = requestAnimationFrame(() => {
      highlightedCommentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [loading, highlightedCommentId]);

  const refetch = () =>
    onList(ticketId, taskId)
      .then(setComments)
      .catch(() => setError(true));

  const handleSubmit = async (
    content: string,
    replyTo?: number,
    newFiles?: CommentNewFiles
  ) => {
    if (!currentUserId) return;
    const ticketItem =
      taskId ??
      (replyTo !== undefined ? (replyingTo?.ticketItem ?? undefined) : undefined);
    await onCreate(ticketId, content, currentUserId, replyTo, ticketItem, newFiles);
    setReplyingTo(null);
    await refetch();
  };

  const handleEdit = async (id: string, content: string) => {
    await onUpdate(id, content);
    await refetch();
  };

  const handleAttachFile = async (file: File, comment: CommentEntry) => {
    if (!comment.id) return;
    await onAttachFileToComment(file, {
      ticketId: comment.ticket ?? ticketId,
      ticketCommentId: comment.id,
      ticketItemId: comment.ticket_item ?? undefined,
    });
    await refetch();
  };

  const handleDelete = async (id: string) => {
    await onDelete(id);
    await refetch();
  };

  const setReactionPending = (pendingKey: string, pending: boolean) => {
    if (pending) {
      pendingReactionKeysRef.current.add(pendingKey);
    } else {
      pendingReactionKeysRef.current.delete(pendingKey);
    }
    setPendingReactionKeys([...pendingReactionKeysRef.current]);
  };

  const updateCommentReactions = (
    commentId: string,
    update: (reactions: CommentReaction[]) => CommentReaction[]
  ) =>
    setComments((currentComments) =>
      currentComments.map((comment) =>
        comment.id === commentId
          ? { ...comment, reactions: update(comment.reactions ?? []) }
          : comment
      )
    );

  const removeOwnReaction = async (commentId: string, ownReaction: CommentReaction) => {
    const ownReactionId = ownReaction.id;
    if (!ownReactionId) return;
    updateCommentReactions(commentId, (reactions) =>
      reactions.filter((reaction) => reaction.id !== ownReactionId)
    );
    const result = await onRemoveReaction(ownReactionId);
    // A 404 means the reaction is already gone, so the optimistic removal stands
    if (!result.succeeded && result.status !== 404) {
      updateCommentReactions(commentId, (reactions) => [...reactions, ownReaction]);
    }
  };

  const addOwnReaction = async (commentId: string, emoji: EmojiEnum, userId: number) => {
    const placeholderId = `pending:${buildPendingReactionKey(commentId, emoji)}`;
    updateCommentReactions(commentId, (reactions) => [
      ...reactions,
      { id: placeholderId, emoji, user: { id: userId, username: "" } },
    ]);
    const result = await onAddReaction(commentId, emoji, userId);
    const createdReactionId = result.succeeded ? result.reactionId : undefined;
    if (createdReactionId) {
      updateCommentReactions(commentId, (reactions) =>
        reactions.map((reaction) =>
          reaction.id === placeholderId ? { ...reaction, id: createdReactionId } : reaction
        )
      );
    } else if (result.succeeded || result.status === 409) {
      // The reaction exists server-side but its id is unknown here
      await refetch();
    } else {
      updateCommentReactions(commentId, (reactions) =>
        reactions.filter((reaction) => reaction.id !== placeholderId)
      );
    }
  };

  const handleToggleReaction = async (comment: CommentEntry, emoji: EmojiEnum) => {
    const commentId = comment.id;
    if (!commentId || !currentUserId) return;
    const pendingKey = buildPendingReactionKey(commentId, emoji);
    if (pendingReactionKeysRef.current.has(pendingKey)) return;
    setReactionPending(pendingKey, true);
    try {
      const ownReaction = comment.reactions?.find(
        (reaction) => reaction.emoji === emoji && reaction.user.id === currentUserId
      );
      if (ownReaction) {
        await removeOwnReaction(commentId, ownReaction);
      } else {
        await addOwnReaction(commentId, emoji, currentUserId);
      }
    } finally {
      setReactionPending(pendingKey, false);
    }
  };

  const pendingReactionEmojisFor = (commentId: string | undefined) =>
    commentId
      ? pendingReactionKeys
          .filter((pendingKey) => pendingKey.startsWith(`${commentId}:`))
          .map((pendingKey) => pendingKey.slice(commentId.length + 1) as EmojiEnum)
      : [];

  const handleReply = (user: CommentAuthor, ticketItem?: string | null) => {
    const nameParts = [user.first_name?.trim(), user.last_name?.trim()].filter(Boolean);
    const name = nameParts.length > 0 ? nameParts.join(" ") : user.username;
    setReplyingTo({ id: user.id, name, ticketItem });
    setTimeout(() => {
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      composerRef.current?.querySelector("textarea")?.focus();
    }, 0);
  };

  if (loading) {
    return (
      <Flex vertical gap={16}>
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Failed to load comments" />
    );
  }

  const displayComments = messengerLayout ? [...comments].reverse() : comments;

  return (
    <Flex vertical gap={0}>
      {comments.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No comments yet" />
      ) : (
        <Flex vertical gap={0}>
          {displayComments.map((comment, index) => {
            const isHighlighted =
              highlightedCommentId !== undefined && comment.id === highlightedCommentId;
            return (
              <div key={comment.id ?? index}>
                <div
                  ref={isHighlighted ? highlightedCommentRef : undefined}
                  className={isHighlighted ? styles.highlightedComment : undefined}
                >
                  <CommentItem
                    comment={comment}
                    currentUserId={currentUserId}
                    onReply={handleReply}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAttachFile={handleAttachFile}
                    onToggleReaction={handleToggleReaction}
                    pendingReactionEmojis={pendingReactionEmojisFor(comment.id)}
                    getDownloadUrl={getDownloadUrl}
                    intl={intl}
                    taskMap={taskMap}
                    messengerLayout={messengerLayout}
                  />
                </div>
                {!messengerLayout && index < displayComments.length - 1 && (
                  <Divider style={{ margin: "12px 0" }} />
                )}
              </div>
            );
          })}
        </Flex>
      )}
      <Flex
        vertical
        ref={composerRef}
        className={stickyComposer ? styles.stickyComposer : undefined}
      >
        <Divider style={{ margin: "16px 0 12px" }} />
        <CommentComposer
          onSubmit={handleSubmit}
          onUploadAttachment={onUploadAttachment}
          replyingTo={replyingTo}
          onClearReply={() => setReplyingTo(null)}
          intl={intl}
        />
      </Flex>
    </Flex>
  );
};
